import React from 'react';
import PropTypes from 'prop-types';
import { navigate } from 'gatsby';
import cx from 'classnames';
import anime from 'animejs';

import { searchSite } from '../../data/searchIndex';
import { withStyles } from '../../tools/withStyles';
import { withSounds } from '../../tools/withSounds';
import { Text } from '../Text';
import { styles } from './SearchModal.styles';

class SearchModal extends React.Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    sounds: PropTypes.object
  };

  state = {
    isOpen: false,
    isClosing: false,
    query: '',
    results: searchSite(''),
    selectedIndex: 0
  };

  componentDidMount () {
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.onGlobalKeyDown);
      window.addEventListener('open-search', this.open);
    }
  }

  componentWillUnmount () {
    clearTimeout(this.closeTimer);
    if (this.overlayElement) anime.remove(this.overlayElement);
    if (this.modalElement) anime.remove(this.modalElement);

    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.onGlobalKeyDown);
      window.removeEventListener('open-search', this.open);
    }
  }

  onGlobalKeyDown = (event) => {
    // Open on Ctrl+K / Cmd+K or slash /
    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
      event.preventDefault();
      this.toggle();
      return;
    }

    if (this.state.isOpen) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.moveSelection(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.moveSelection(-1);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const { results, selectedIndex } = this.state;
        if (results[selectedIndex]) {
          this.selectItem(results[selectedIndex]);
        }
      }
    }
  }

  playSound = (name) => {
    const { sounds } = this.props;
    const sound = sounds && sounds[name];
    if (sound) {
      sound.play();
    }
  }

  stopSound = (name) => {
    const { sounds } = this.props;
    const sound = sounds && sounds[name];
    if (sound) {
      sound.stop();
    }
  }

  animateIn = () => {
    if (!this.modalElement || !this.overlayElement) return;

    anime.remove(this.overlayElement);
    anime.remove(this.modalElement);

    anime({
      targets: this.overlayElement,
      opacity: [0, 1],
      easing: 'easeOutCubic',
      duration: 250
    });

    anime({
      targets: this.modalElement,
      easing: 'easeOutCubic',
      duration: 400,
      keyframes: [
        { opacity: 0.95, scale: 0.97, translateY: -8, duration: 130 },
        { opacity: 0.15, scale: 0.985, translateY: -4, duration: 80 },
        { opacity: 1, scale: 1, translateY: 0, duration: 190 }
      ]
    });

    if (this.resultsRef && this.resultsRef.children && this.resultsRef.children.length > 0) {
      anime.remove(this.resultsRef.children);
      anime({
        targets: this.resultsRef.children,
        opacity: [0, 1],
        translateX: [-16, 0],
        delay: anime.stagger(25),
        duration: 250,
        easing: 'easeOutCubic'
      });
    }
  }

  open = () => {
    clearTimeout(this.closeTimer);
    this.playSound('fade');
    this.setState({
      isOpen: true,
      isClosing: false,
      query: '',
      results: searchSite(''),
      selectedIndex: 0
    }, () => {
      this.animateIn();
      if (this.inputRef) {
        setTimeout(() => this.inputRef.focus(), 60);
      }
    });
  }

  close = () => {
    if (!this.state.isOpen || this.state.isClosing) return;
    this.playSound('fade');
    this.setState({ isClosing: true });

    if (this.modalElement && this.overlayElement) {
      anime.remove(this.overlayElement);
      anime.remove(this.modalElement);

      anime({
        targets: this.modalElement,
        opacity: 0,
        scale: 0.97,
        translateY: -10,
        easing: 'easeInCubic',
        duration: 180
      });

      anime({
        targets: this.overlayElement,
        opacity: 0,
        easing: 'easeInCubic',
        duration: 180,
        complete: () => {
          this.setState({ isOpen: false, isClosing: false });
        }
      });
    } else {
      this.setState({ isOpen: false, isClosing: false });
    }
  }

  toggle = () => {
    if (this.state.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  onQueryChange = (event) => {
    const query = event.target.value;
    const results = searchSite(query);

    this.playSound('hover');

    this.setState({
      query,
      results,
      selectedIndex: 0
    }, () => {
      if (this.resultsRef && this.resultsRef.children && this.resultsRef.children.length > 0) {
        anime.remove(this.resultsRef.children);
        anime({
          targets: this.resultsRef.children,
          opacity: [0, 1],
          translateX: [-10, 0],
          delay: anime.stagger(18),
          duration: 180,
          easing: 'easeOutCubic'
        });
      }
    });
  }

  moveSelection = (delta) => {
    const { results, selectedIndex } = this.state;
    if (!results.length) return;

    this.playSound('hover');
    let nextIndex = selectedIndex + delta;
    if (nextIndex < 0) nextIndex = results.length - 1;
    if (nextIndex >= results.length) nextIndex = 0;

    this.setState({ selectedIndex: nextIndex }, () => {
      if (this.resultsRef && this.resultsRef.children[nextIndex]) {
        this.resultsRef.children[nextIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    });
  }

  selectItem = (item) => {
    this.playSound('click');
    this.close();

    if (!item || !item.url) return;

    if (item.isExternal) {
      if (item.url.startsWith('mailto:')) {
        window.location.href = item.url;
      } else {
        window.open(item.url, '_blank', 'noreferrer');
      }
    } else {
      if (item.url.includes('#')) {
        const [path, hash] = item.url.split('#');
        if (typeof window !== 'undefined' && (window.location.pathname === path || (path === '/projects' && window.location.pathname.startsWith('/projects')))) {
          window.location.hash = hash;
          window.dispatchEvent(new Event('popstate'));
        } else {
          navigate(item.url);
        }
      } else {
        navigate(item.url);
      }
    }
  }

  render () {
    const { classes } = this.props;
    const { isOpen, query, results, selectedIndex } = this.state;

    if (!isOpen) {
      return null;
    }

    return (
      <div
        ref={ref => (this.overlayElement = ref)}
        className={classes.overlay}
        onClick={this.close}
        role='dialog'
        aria-modal='true'
        aria-label='Site Command Search'
      >
        <div
          ref={ref => (this.modalElement = ref)}
          className={classes.modal}
          onClick={e => e.stopPropagation()}
        >
          <div className={classes.terminalBar}>
            <span className={classes.terminalStatus}>ESC TO CLOSE</span>
          </div>

          <header className={classes.header}>
            <span className={classes.prompt}>&gt;</span>
            <input
              ref={ref => (this.inputRef = ref)}
              className={classes.input}
              type='text'
              value={query}
              onChange={this.onQueryChange}
              placeholder='Search...'
              aria-label='Search whole website'
              autoComplete='off'
              spellCheck='false'
            />
          </header>

          <div
            className={classes.results}
            ref={ref => (this.resultsRef = ref)}
          >
            {results.length > 0 ? (
              results.map((item, index) => {
                const isActive = index === selectedIndex;
                return (
                  <div
                    key={item.id}
                    className={cx(classes.item, isActive && classes.itemActive)}
                    onClick={() => this.selectItem(item)}
                    onMouseEnter={() => this.setState({ selectedIndex: index })}
                  >
                    <div className={classes.itemLeft}>
                      <h4 className={classes.itemTitle}>
                        <Text
                          key={`t_${item.id}_${query}`}
                          animation={{ animate: true, show: true, independent: true }}
                          stableTime
                          typingDuration={160}
                        >
                          {item.title}
                        </Text>
                      </h4>
                      <p className={classes.itemSubtitle}>
                        <Text
                          key={`s_${item.id}_${query}`}
                          animation={{ animate: true, show: true, independent: true }}
                          stableTime
                          typingDuration={220}
                        >
                          {item.subtitle}
                        </Text>
                      </p>
                    </div>
                    <div className={classes.itemRight}>
                      <span className={classes.categoryBadge}>
                        <Text
                          key={`c_${item.id}_${query}`}
                          animation={{ animate: true, show: true, independent: true }}
                          stableTime
                          typingDuration={130}
                        >
                          {item.category}
                        </Text>
                      </span>
                      <span className={classes.enterBadge}>
                        <span className={classes.arrowIcon}>↵</span>
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={classes.empty}>
                <Text
                  key={`empty_${query}`}
                  animation={{ animate: true, show: true, independent: true }}
                  stableTime
                  typingDuration={180}
                >
                  {`NO RESULTS FOUND FOR "${query}"`}
                </Text>
              </div>
            )}
          </div>

          <footer className={classes.footer}>
            <div className={classes.shortcuts}>
              <div className={classes.shortcutGroup}>
                <span className={classes.shortcutKey}>↑</span>
                <span className={classes.shortcutKey}>↓</span>
                <span className={classes.shortcutLabel}>Navigate</span>
              </div>
              <div className={classes.shortcutGroup}>
                <span className={classes.shortcutKey}>ENTER</span>
                <span className={classes.shortcutLabel}>Open</span>
              </div>
              <div className={classes.shortcutGroup}>
                <span className={classes.shortcutKey}>ESC</span>
                <span className={classes.shortcutLabel}>Close</span>
              </div>
            </div>
            <div className={classes.resultsCount}>
              <span>{results.length} results</span>
            </div>
          </footer>
        </div>
      </div>
    );
  }
}

export { SearchModal };
export default withStyles(styles)(withSounds()(SearchModal));
